import type { Inventory } from '../inventory/Inventory';
import type { InputManager } from '../core/InputManager';
import { ITEMS, type ItemId } from '../config/gameConfig';

export type Screen = 'loading' | 'title' | 'playing' | 'paused' | 'settings' | 'examine' | 'clear';

export class UIManager {
  private screens: Record<string, HTMLElement>;
  private loadBar: HTMLElement;
  private loadText: HTMLElement;
  private promptEl: HTMLElement;
  private toastEl: HTMLElement;
  private itemPopup: HTMLElement;
  private itemName: HTMLElement;
  private itemDesc: HTMLElement;
  private hintBox: HTMLElement;
  private hintText: HTMLElement;
  private objectiveText: HTMLElement;
  private invBar: HTMLElement;
  private crosshair: HTMLElement;
  private examineTitle: HTMLElement;
  private examineBody: HTMLElement;
  private examineExtra: HTMLElement;
  private clearTime: HTMLElement;
  private hintBtn: HTMLElement;
  private hud: HTMLElement;
  private mobileControls: HTMLElement;

  hintsEnabled = true;
  private toastTimer = 0;
  private popupTimer = 0;
  private settingsBackTo: Screen = 'paused';

  onStart: (() => void) | null = null;
  onResume: (() => void) | null = null;
  onToTitle: (() => void) | null = null;
  onReplay: (() => void) | null = null;
  onVolumeBgm: ((v: number) => void) | null = null;
  onVolumeSfx: ((v: number) => void) | null = null;
  onSensitivity: ((v: number) => void) | null = null;
  onToggleHints: (() => void) | null = null;
  onExamineClose: (() => void) | null = null;
  onSelectItem: ((id: ItemId) => void) | null = null;
  onExamineAction: ((action: string, payload?: string) => void) | null = null;

  constructor() {
    const $ = (id: string) => document.getElementById(id)!;
    this.screens = {
      loading: $('screen-loading'),
      title: $('screen-title'),
      pause: $('screen-pause'),
      settings: $('screen-settings'),
      clear: $('screen-clear'),
    };
    this.loadBar = $('load-bar');
    this.loadText = $('load-text');
    this.promptEl = $('prompt');
    this.toastEl = $('toast');
    this.itemPopup = $('item-popup');
    this.itemName = $('item-popup-name');
    this.itemDesc = $('item-popup-desc');
    this.hintBox = $('hint-box');
    this.hintText = $('hint-text');
    this.objectiveText = $('objective-text');
    this.invBar = $('inv-bar');
    this.crosshair = $('crosshair');
    this.examineTitle = $('examine-title');
    this.examineBody = $('examine-body');
    this.examineExtra = $('examine-extra');
    this.clearTime = $('clear-time');
    this.hintBtn = $('btn-hints-toggle');
    this.hud = $('hud');
    this.mobileControls = $('mobile-controls');

    $('btn-start').onclick = () => this.onStart?.();
    $('btn-resume').onclick = () => this.onResume?.();
    $('btn-to-title').onclick = () => this.onToTitle?.();
    $('btn-replay').onclick = () => this.onReplay?.();
    $('btn-settings').onclick = () => this.openSettings('paused');
    $('btn-title-settings').onclick = () => this.openSettings('title');
    $('btn-settings-back').onclick = () => this.closeSettings();
    $('btn-hints-toggle').onclick = () => {
      this.hintsEnabled = !this.hintsEnabled;
      this.hintBtn.textContent = `ヒント: ${this.hintsEnabled ? 'ON' : 'OFF'}`;
      this.onToggleHints?.();
      this.updateHintVisibility();
    };
    $('btn-examine-close').onclick = () => this.onExamineClose?.();
    $('btn-fullscreen').onclick = () => {
      if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
      else document.exitFullscreen?.();
    };

    const volBgm = $('vol-bgm') as HTMLInputElement;
    const volSfx = $('vol-sfx') as HTMLInputElement;
    const sens = $('sens') as HTMLInputElement;
    volBgm.oninput = () => this.onVolumeBgm?.(Number(volBgm.value) / 100);
    volSfx.oninput = () => this.onVolumeSfx?.(Number(volSfx.value) / 100);
    sens.oninput = () => this.onSensitivity?.(Number(sens.value) / 10);
  }

  setupMobile(input: InputManager): void {
    if (!input.mobile) return;
    this.mobileControls.classList.remove('hidden');

    const zone = document.getElementById('joy-zone')!;
    const knob = document.getElementById('joy-knob')!;
    const maxR = 40;

    const joyStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      const rect = zone.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const move = (ev: TouchEvent) => {
        const p = ev.changedTouches[0];
        let dx = p.clientX - cx;
        let dy = p.clientY - cy;
        const len = Math.hypot(dx, dy);
        if (len > maxR) {
          dx = (dx / len) * maxR;
          dy = (dy / len) * maxR;
        }
        knob.style.transform = `translate(${dx}px, ${dy}px)`;
        input.setJoystick(dx / maxR, dy / maxR);
      };
      const end = () => {
        knob.style.transform = 'translate(0,0)';
        input.setJoystick(0, 0);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', end);
      };
      window.addEventListener('touchmove', move, { passive: false });
      window.addEventListener('touchend', end);
      move(e);
    };
    zone.addEventListener('touchstart', joyStart, { passive: false });

    // 右半分ドラッグで視点
    const canvas = document.getElementById('game-canvas')!;
    canvas.addEventListener(
      'touchstart',
      (e) => {
        const t = e.changedTouches[0];
        if (t.clientX > window.innerWidth * 0.4) {
          input.startLookTouch(t.clientX, t.clientY);
        }
      },
      { passive: true },
    );
    canvas.addEventListener(
      'touchmove',
      (e) => {
        if (!input.lookTouchActive) return;
        const t = e.changedTouches[0];
        input.moveLookTouch(t.clientX, t.clientY);
      },
      { passive: true },
    );
    canvas.addEventListener('touchend', () => input.endLookTouch());

    document.getElementById('btn-interact')!.onclick = () => input.pressInteract();
    document.getElementById('btn-use')!.onclick = () => input.pressUse();
    document.getElementById('btn-pause-m')!.onclick = () => input.pressPause();
  }

  setLoadProgress(p: number, text?: string): void {
    this.loadBar.style.width = `${Math.floor(p * 100)}%`;
    if (text) this.loadText.textContent = text;
  }

  showScreen(name: Screen): void {
    for (const el of Object.values(this.screens)) el.classList.add('hidden');
    document.getElementById('examine')!.classList.add('hidden');

    if (name === 'loading') this.screens.loading.classList.remove('hidden');
    if (name === 'title') this.screens.title.classList.remove('hidden');
    if (name === 'paused') this.screens.pause.classList.remove('hidden');
    if (name === 'settings') this.screens.settings.classList.remove('hidden');
    if (name === 'clear') this.screens.clear.classList.remove('hidden');
    if (name === 'examine') document.getElementById('examine')!.classList.remove('hidden');

    if (name === 'playing') {
      this.hud.classList.remove('hidden');
    } else if (name === 'paused' || name === 'settings' || name === 'examine') {
      this.hud.classList.remove('hidden');
    } else {
      this.hud.classList.add('hidden');
    }
  }

  openSettings(from: Screen): void {
    this.settingsBackTo = from;
    this.showScreen('settings');
  }

  closeSettings(): void {
    if (this.settingsBackTo === 'title') this.showScreen('title');
    else if (this.settingsBackTo === 'paused') this.showScreen('paused');
    else this.showScreen('playing');
  }

  setPrompt(text: string | null, active = false): void {
    if (!text) {
      this.promptEl.classList.add('hidden');
      this.crosshair.classList.remove('active');
      return;
    }
    this.promptEl.textContent = text;
    this.promptEl.classList.remove('hidden');
    if (active) this.crosshair.classList.add('active');
    else this.crosshair.classList.remove('active');
  }

  toast(msg: string, duration = 2.5): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.remove('hidden');
    this.toastTimer = duration;
  }

  showItemGet(id: ItemId): void {
    const item = ITEMS[id];
    this.itemName.textContent = item.name;
    this.itemDesc.textContent = item.description;
    this.itemPopup.classList.remove('hidden');
    this.popupTimer = 2.8;
  }

  setObjective(text: string): void {
    this.objectiveText.textContent = text;
  }

  setHint(text: string): void {
    this.hintText.textContent = text;
    this.updateHintVisibility();
  }

  updateHintVisibility(): void {
    if (this.hintsEnabled && this.hintText.textContent) this.hintBox.classList.remove('hidden');
    else this.hintBox.classList.add('hidden');
  }

  refreshInventory(inv: Inventory): void {
    this.invBar.innerHTML = '';
    const list = inv.list();
    if (list.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'inv-slot empty';
      empty.textContent = '—';
      this.invBar.appendChild(empty);
      return;
    }
    for (const item of list) {
      const slot = document.createElement('div');
      slot.className = 'inv-slot' + (inv.selected === item.id ? ' selected' : '');
      slot.textContent = `${item.icon}\n${item.name}`;
      slot.title = item.description;
      slot.onclick = () => this.onSelectItem?.(item.id);
      this.invBar.appendChild(slot);
    }
  }

  openExamine(title: string, body: string, extraHtml = ''): void {
    this.examineTitle.textContent = title;
    this.examineBody.textContent = body;
    this.examineExtra.innerHTML = extraHtml;
    this.showScreen('examine');

    // 動的ボタンにハンドラ
    this.examineExtra.querySelectorAll('[data-action]').forEach((btn) => {
      (btn as HTMLElement).onclick = () => {
        const action = (btn as HTMLElement).dataset.action!;
        const payload = (btn as HTMLElement).dataset.payload;
        this.onExamineAction?.(action, payload);
      };
    });
  }

  updateExamineBody(body: string): void {
    this.examineBody.textContent = body;
  }

  updateExamineExtra(html: string): void {
    this.examineExtra.innerHTML = html;
    this.examineExtra.querySelectorAll('[data-action]').forEach((btn) => {
      (btn as HTMLElement).onclick = () => {
        const action = (btn as HTMLElement).dataset.action!;
        const payload = (btn as HTMLElement).dataset.payload;
        this.onExamineAction?.(action, payload);
      };
    });
  }

  showClear(seconds: number): void {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    this.clearTime.textContent = `クリア時間  ${m}分${s.toString().padStart(2, '0')}秒`;
    this.showScreen('clear');
  }

  update(dt: number): void {
    if (this.toastTimer > 0) {
      this.toastTimer -= dt;
      if (this.toastTimer <= 0) this.toastEl.classList.add('hidden');
    }
    if (this.popupTimer > 0) {
      this.popupTimer -= dt;
      if (this.popupTimer <= 0) this.itemPopup.classList.add('hidden');
    }
  }
}
